import React, {useEffect, useMemo, useRef, useState} from "react";
import moment from "moment";
import {useHistory} from "react-router-dom";
import Button from "@source/components/UI/buttons/button/button";
import SearchInput from "@source/components/UI/inputs/searchInput/searchInput";
import SelectInput, {ISelectItem} from "@source/components/UI/inputs/selectInput/selectInput";
import DateRangePicker from "@source/components/UI/inputs/dateRangePicker/dateRangePicker";
import Svg from "@source/components/UI/svg/svg";
import {IFillVariant} from "@source/utils/enums";
import Notification from "@source/components/snackMessage/notification";
import ConsentController from "@source/api/controllers/consentController";
import {
  ConsentTemplate,
  ConsentTemplateStatus,
  ConsentTemplateQuery,
} from "@source/api/models/consentModels";
import useIncrementalConsentList from "@source/hooks/useIncrementalConsentList";
import {
  ConsentAutoLoad,
  ConsentFilterBar,
  ConsentRequestState,
} from "@source/components/consentDocuments";
import {HOME_ROUTES} from "@source/utils/utils";
import {consentTemplateBuilderPath} from "@source/api/models/consentHelpers";
import iconEditResident from "@source/assets/icons/iconEditResident.svg";
import iconDoc from "@source/assets/icons/iconDoc.svg";
import iconPlus from "@source/assets/icons/iconPlus.svg";
import {formatConsentDate} from "@source/components/consentDocuments/consentDate";
import styles from "./templateLibrary.module.scss";

type DateRangeValue = [Date, Date] | [Date] | null;

export const createTemplateListQuery = (
  status: ConsentTemplateStatus,
  search: string,
  dates: DateRangeValue,
): ConsentTemplateQuery => {
  const [start, end] = dates || [];
  return {
    status,
    search: search.trim() || undefined,
    startDate: start ? moment(start).startOf("day").toISOString() : undefined,
    endDate: end ? moment(end).endOf("day").toISOString() : undefined,
    sort: "desc",
  };
};

const TemplateStatus = ({status}: {status: ConsentTemplateStatus}) => (
  <span className={`${styles.status} ${status === ConsentTemplateStatus.ACTIVE ? styles.statusActive : styles.statusArchived}`}>
    {status === ConsentTemplateStatus.ACTIVE ? "Active" : "Archived"}
  </span>
);

const statusItems: ISelectItem[] = [
  {id: ConsentTemplateStatus.ACTIVE, value: ConsentTemplateStatus.ACTIVE, title: "Active"},
  {id: ConsentTemplateStatus.ARCHIVED, value: ConsentTemplateStatus.ARCHIVED, title: "Archived"},
];

export const TemplateLibrary = ({
  organizationId,
  canManage,
}: {
  organizationId: string;
  canManage: boolean;
}) => {
  const history = useHistory();
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(ConsentTemplateStatus.ACTIVE);
  const [dates, setDates] = useState<DateRangeValue>(null);

  useEffect(() => {
    const timeout = window.setTimeout(() => setSearch(searchText), 500);
    return () => window.clearTimeout(timeout);
  }, [searchText]);

  const filters = useMemo(
    () => createTemplateListQuery(status, search, dates),
    [dates, search, status],
  );
  const request = useMemo(
    () => (query: ConsentTemplateQuery & {page: number; limit: number}) =>
      ConsentController.listTemplates(organizationId, query),
    [organizationId],
  );
  const list = useIncrementalConsentList<ConsentTemplate, ConsentTemplateQuery>(request, filters);
  const clearFilters = () => {
    setSearchText("");
    setSearch("");
    setStatus(ConsentTemplateStatus.ACTIVE);
    setDates(null);
  };
  const hasFilters = Boolean(searchText || status !== ConsentTemplateStatus.ACTIVE || dates?.length);

  return (
    <>
      <ConsentFilterBar onClear={hasFilters ? clearFilters : undefined}>
        <SearchInput id="template-search" className={styles.search} value={searchText} onChange={(event) => setSearchText(event.target.value)} placeHolder="Search" fill={IFillVariant.Fill}/>
        <SelectInput id="template-status" className={styles.filterControl} value={status} items={statusItems} handleChange={(event) => setStatus(event.target.value as ConsentTemplateStatus)}/>
        <DateRangePicker id="template-date-range" className={styles.dateRange} value={dates} onChange={setDates} placeholder="Date range" fill={IFillVariant.Fill} maxDate={new Date()}/>
      </ConsentFilterBar>
      {list.isLoading && !list.items.length ? <ConsentRequestState variant="loading" title="Loading templates"/> : null}
      {list.error && !list.items.length ? <ConsentRequestState variant="error" title="Unable to load templates" description={list.error} onRetry={list.retry}/> : null}
      {!list.isLoading && !list.error && !list.items.length ? (
        <ConsentRequestState variant="empty" title={hasFilters ? "No matching templates" : "No templates yet"} description={hasFilters ? "Try changing or clearing your filters." : "Create a template by copying an existing one."}/>
      ) : null}
      {list.items.length ? <div className={styles.templateList}>
        {list.items.map((template) => <article key={template._id} className={styles.templateCard}>
          <div className={styles.templateCardContent}>
            <div className={styles.cardMain}>
              <h3>{template.title}</h3>
              <div className={styles.meta}>
                <span>Created {formatConsentDate(template.createdAt)}</span>
              </div>
            </div>
            <div className={styles.cardAside}>
              {template.status === ConsentTemplateStatus.ARCHIVED ? <TemplateStatus status={template.status}/> : null}
              {canManage && template.status === ConsentTemplateStatus.ACTIVE ? <button type="button" className={styles.editFamilyMember} onClick={() => history.push(consentTemplateBuilderPath(template._id))}>
                <Svg className={styles.icon} src={iconEditResident} color="#3956C7" width={16} height={16}/>
                <span className={styles.text}>Edit</span>
              </button> : null}
            </div>
          </div>
        </article>)}
      </div> : null}
      {list.error && list.items.length ? <ConsentRequestState variant="error" title="Unable to load more templates" description={list.error} onRetry={list.retry}/> : null}
      {list.items.length ? <ConsentAutoLoad hasMore={list.hasMore} isLoading={list.isLoading} error={list.error} onLoadMore={list.loadMore}/> : null}
    </>
  );
};

